import { Amount, Box, Button, Link, Text, useToast } from '@razorpay/blade/components';
import api from 'api';
import { getErrorsAsString } from 'components/Kyc/utils';
import ComponentSection from 'components/PayDetails/components/generics/ComponentSection';
import { routePaths } from 'components/Routes/data';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import { format } from 'date-fns';
import { useFormWithProvider } from 'forms';
import { useSearchParams } from 'hooks/useSearchParams';
import React, { FormEvent } from 'react';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { AppError } from 'utils/AppError';
import CtcPreview from './components/CtcPreview';
import { SummaryView, SummaryViewItem } from './components/SummaryView';
import { ReviseSalaryFormData, reviseSalarySchema } from './constants';
import { createUpdatePayPayload, createUpdatePayWithSalaryStructurePayload } from './utils';
import { SalaryStructure } from 'components/SalaryStructure/types';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import ArrearsPreview from './components/ArrearsPreview';

const ReviewStep = ({ salaryStructure }: { salaryStructure?: SalaryStructure }) => {
  const history = useHistory();
  const toast = useToast();

  const { validate, formData } = useFormWithProvider<ReviseSalaryFormData>();

  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId) as number;
  const userId = useSearchParams().urlParams.get('userId') ?? loggedInUserId.toString();
  const { isM3S1Enabled } = useIsFeatureDisabledForRearch();

  const hasStructureChange = formData.ctcData?.hasStructureChange ?? false;

  const updateSalaryMutation = useMutation({
    mutationFn: api.compensationMutations.updatePay,
    onSuccess: () => {
      toast.show({
        content: 'Salary details updated successfully',
        color: 'positive',
        duration: 3000,
      });
      history.push({
        pathname:
          !userId || userId === loggedInUserId.toString()
            ? routePaths.payDetails.compensation.myCompensation.root
            : routePaths.payDetails.compensation.root,
        search: `?userId=${userId}`,
      });
    },
    onError: (e: AppError) => {
      toast.show({
        content: getErrorsAsString(e),
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const updateSalaryWithSalaryStructureMutation = useMutation({
    mutationFn: api.compensationMutations.updatePayWithSalaryStructure,
    onSuccess: () => {
      toast.show({
        content: 'Salary details updated successfully',
        color: 'positive',
        duration: 3000,
      });
      history.push({
        pathname:
          !userId || userId === loggedInUserId.toString()
            ? routePaths.payDetails.compensation.myCompensation.root
            : routePaths.payDetails.compensation.root,
        search: `?userId=${userId}`,
      });
    },
    onError: (e: AppError) => {
      toast.show({
        content: getErrorsAsString(e),
        color: 'negative',
        duration: 3000,
      });
    },
  });

  if (!formData.revisedCompensation) {
    return (
      <Redirect
        to={{
          pathname: routePaths.payDetails.compensation.edit.salaryDetails,
          search: userId ? `?userId=${userId}` : '',
        }}
      />
    );
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (isM3S1Enabled) {
      updateSalaryWithSalaryStructureMutation.mutate(
        createUpdatePayWithSalaryStructurePayload({
          userId: userId!,
          requestId: formData.ctcData?.requestId ?? '',
          annualSalary: parseInt(formData.revisedCompensation ?? '0'),
          effectiveFrom: formData.effectivePayrollMonth!,
          salaryStructureId: hasStructureChange
            ? formData.salaryStructureId?.[0]!
            : undefined,
          hasStructureChange: hasStructureChange
            ? hasStructureChange
            : undefined,
        }),
      );
    } else {
      updateSalaryMutation.mutate(
        createUpdatePayPayload({
          userId: userId!,
          annualSalary: parseInt(formData.revisedCompensation ?? '0'),
          effectiveFrom: formData.effectivePayrollMonth!,
        }),
      );
    }
  };

  const isFormValid = validate(reviseSalarySchema, { sideEffects: false });
  const isLoading =
    updateSalaryMutation.isLoading || updateSalaryWithSalaryStructureMutation.isLoading;

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <StepContentWrapper maxWidth="600px" noBgIllustration>
        <SummaryView>
          <SummaryViewItem label="Revised CTC">
            <Amount
              size="large"
              value={parseInt(formData.revisedCompensation ?? '0')}
              suffix="none"
            />
          </SummaryViewItem>
          {!isM3S1Enabled ? (
            <SummaryViewItem label="Monthly CTC">
              <Amount
                size="large"
                value={parseInt(formData.revisedCompensation ?? '0') / 12}
                suffix="none"
              />
            </SummaryViewItem>
          ) : null}
          <SummaryViewItem label="Effective from">
            <Text size="large">
              {formData.effectiveFrom ? format(formData.effectiveFrom, 'dd MMMM, yyyy') : ''}
            </Text>
          </SummaryViewItem>
          {formData.ctcData?.hasStructureChange ? (
            <SummaryViewItem label="Revised Structure">
              <Link
                href={
                  salaryStructure?.id
                    ? `${routePaths.salaryStructures.root}?structureId=${salaryStructure?.id}`
                    : routePaths.salaryStructures.root
                }
                size="large"
                target="_blank">
                {salaryStructure?.name ?? 'Salary Structure'}
              </Link>
            </SummaryViewItem>
          ) : null}
          {isM3S1Enabled && formData.ctcData?.arrears && formData.ctcData?.arrears.data.length ? (
            <SummaryViewItem label="Arrear amount">
              <Link
                size="large"
                href="#arrears-preview-container"
                accessibilityLabel="Scroll to arrears breakdown">
                {String(formData.ctcData?.arrears?.totalYearlyAmount || 0)}
              </Link>
            </SummaryViewItem>
          ) : null}
        </SummaryView>
        <Box display="flex" flexDirection="column" gap="spacing.7">
          <ComponentSection marginTop="spacing.9" marginBottom="spacing.8">
            {formData.ctcData ? <CtcPreview ctcData={formData.ctcData} /> : null}
          </ComponentSection>
          {isM3S1Enabled && formData.ctcData?.arrears && formData.ctcData.arrears.data.length ? (
            <Box id="arrears-preview-container">
              <ArrearsPreview ctcData={formData.ctcData} />
            </Box>
          ) : null}
        </Box>
      </StepContentWrapper>
      <StepActionsWrapper maxWidth="600px">
        <Button variant="secondary" onClick={() => history.goBack()} isDisabled={isLoading}>
          Back
        </Button>
        <Button type="submit" isDisabled={!isFormValid} isLoading={isLoading}>
          Confirm
        </Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};

export default ReviewStep;
